import { createApi } from '@reduxjs/toolkit/query/react';
import { QUERY } from '@constants/query.constants';
import {
  ICommunityConversationParams,
  IConversation,
  IConversationFansQueryParams,
  IConversationQueryParams,
  IConversationResponse,
  IConversationRestrictionOptions,
  IConversationSet,
  ICreateConversation,
  ICreateConversationResponse,
  INormalizedConversationResponse,
  INormalizedFansResponse,
  ITopicConversationParams,
} from '@ui/components';
import { authQuery } from '../config/query.config';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { cookieService } from '@lib/cookie.service';
import {
  IFansOfConverstion,
  IReorderConversationsPayload,
} from '@ui/components/organisms/conversation';

export const conversationsAdapter = createEntityAdapter({
  selectId: (item: IConversationSet) => item.conversation.id,
  sortComparer: (a, b) => {
    const lastMessageA = a?.last_message || a?.conversation?.updated_at || ''; // STill suprised to how this perfectly solves the sort issue
    const lastMessageB = b?.conversation?.updated_at || b?.last_message || '';
    return lastMessageB.localeCompare(lastMessageA);
  },
});
export const conversationFansAdapter = createEntityAdapter({
  selectId: (fan: IFansOfConverstion) => fan.id,
  sortComparer: (a, b) => a.user_name?.localeCompare(b.user_name) || 0,
});

export const topicAdapter = createEntityAdapter({
  selectId: (topic: IConversationSet) => topic.conversation.id,
  sortComparer: (a, b) => {
    const indexA = a.conversation.order_index ?? 0;
    const indexB = b.conversation.order_index ?? 0;

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    const dateA = new Date(a.conversation.created_at).getTime();
    const dateB = new Date(b.conversation.created_at).getTime();

    return dateB - dateA;
  },
});

export const conversationFansSelector = conversationFansAdapter.getSelectors();

export const conversationsSelector = conversationsAdapter.getSelectors();
export const topicSelectors = topicAdapter.getSelectors();

export const conversationsApi = createApi({
  reducerPath: 'conversations-api',
  baseQuery: authQuery,
  tagTypes: [
    QUERY.conversations,
    QUERY.singleConversation,
    QUERY.communityConversations,
    QUERY.conversationFans,
    QUERY.topics,
  ],
  endpoints: (builder) => ({
    getConversations: builder.query<INormalizedConversationResponse, IConversationQueryParams>({
      query: (params) => ({
        url: 'public/v1/forum/conversation',
        params,
      }),
      providesTags: [QUERY.conversations],
      transformResponse: ({ conversations, total_pages }: IConversationResponse) => {
        const initialState = conversationsAdapter.getInitialState();
        const filtered = conversations
          .filter((conversation) => conversation) // somehow null slips through
          .map((conversationSet) => ({
            ...conversationSet,
            conversation: {
              ...conversationSet.conversation,
              main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
            },
          }));

        return {
          conversations: conversationsAdapter.setAll(initialState, filtered),
          totalPages: total_pages,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
        const isSamePage = currentArg.page === previousArg?.page;
        return !isSameCreator || !isSamePage;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.creator_id}`;
      },
      merge: (currentState, incomingState) => {
        conversationsAdapter.addMany(
          currentState.conversations,
          conversationsSelector.selectAll(incomingState.conversations),
        );
      },
    }),

    getTopicConversations: builder.query<INormalizedConversationResponse, ITopicConversationParams>(
      {
        query: (params) => ({
          url: 'public/v1/forum/conversation',
          params,
        }),
        providesTags: [QUERY.topics, QUERY.conversations],
        transformResponse: ({ conversations, total_pages }: IConversationResponse) => {
          const initialState = topicAdapter.getInitialState();
          const filtered = conversations
            .filter((conversation) => conversation) // somehow null slips through
            .map((conversationSet) => ({
              ...conversationSet,
              conversation: {
                ...conversationSet.conversation,
                main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
              },
            }));

          return {
            conversations: topicAdapter.setAll(initialState, filtered),
            totalPages: total_pages,
          };
        },
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
          const isSamePage = currentArg.page === previousArg?.page;
          return !isSameCreator || !isSamePage;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${queryArgs?.creator_id}`;
        },
        merge: (currentState, incomingState) => {
          topicAdapter.addMany(
            currentState.conversations,
            conversationsSelector.selectAll(incomingState.conversations),
          );
        },
      },
    ),
    getCommunityConversations: builder.query<
      INormalizedConversationResponse,
      ICommunityConversationParams
    >({
      query: (params) => ({
        url: 'public/v1/forum/conversation',
        params,
      }),
      providesTags: [QUERY.communityConversations],
      transformResponse: ({ conversations, total_pages }: IConversationResponse, {}) => {
        const initialState = conversationsAdapter.getInitialState();
        const filtered = conversations
          .filter((conversation) => conversation) // somehow null slips through
          .map((conversationSet) => ({
            ...conversationSet,
            conversation: {
              ...conversationSet.conversation,
              main_thread: conversationSet.conversation.main_thread || conversationSet.thread_id, // fallback thread id
            },
          }));

        return {
          conversations: conversationsAdapter.setAll(initialState, filtered),
          totalPages: total_pages,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
        const isSamePage = currentArg.page === previousArg?.page;
        return !isSameCreator || !isSamePage;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.creator_id}`;
      },
      merge: (currentState, incomingState) => {
        conversationsAdapter.addMany(
          currentState.conversations,
          conversationsSelector.selectAll(incomingState.conversations),
        );
      },
    }),
    getSingleConversation: builder.query<IConversationSet, { id: string; creator_id: string }>({
      query: ({ id, creator_id }) => ({
        url: `public/v1/forum/conversation/${id}`,
        params: {
          creator_id,
        },
      }),
      providesTags: [QUERY.singleConversation],
    }),

    createConversation: builder.mutation<ICreateConversationResponse, ICreateConversation>({
      query: (conversation: ICreateConversation) => ({
        url: `public/v1/forum/conversation`,
        method: 'POST',
        body: conversation,
      }),
      invalidatesTags: [QUERY.conversations, QUERY.communityConversations, QUERY.topics],
      async onQueryStarted(conversation, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const creator_id = cookieService.getUserId();

        const optimisticConversation: IConversationSet = {
          conversation: {
            ...data.conversation,
            order_index: -1000, // manually inject to appear first
          },
          restrictions_list: conversation.restrictions,
          associated_fans: 0,
        };

        // Update topic list (if applicable)
        if (conversation.conversation_type === 'topic') {
          dispatch(
            conversationsApi.util.updateQueryData(
              'getTopicConversations',
              { creator_id, page: 1, conversation_type: 'topic' } as any,
              (draft) => {
                topicAdapter.addOne(draft.conversations, optimisticConversation);
              },
            ),
          );
        }

        if (conversation.conversation_type !== 'topic') {
          dispatch(
            conversationsApi.util.updateQueryData(
              'getConversations',
              { creator_id, page: 1 } as any,
              (draft) => {
                conversationsAdapter.setOne(draft.conversations, optimisticConversation);
              },
            ),
          );
        }
      },
    }),

    updateConversation: builder.mutation<
      IConversation,
      Partial<IConversation> & { params: IConversationQueryParams }
    >({
      query: ({ id, ...patch }) => ({
        url: `public/v1/forum/conversation/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted({ id, params, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          conversationsApi.util.updateQueryData('getConversations', params, (draft) => {
            const conversationSet = conversationsSelector.selectById(draft.conversations, id);
            conversationsAdapter.updateOne(draft.conversations, {
              id,
              changes: {
                ...conversationSet,
                conversation: {
                  ...conversationSet?.conversation,
                  ...patch,
                },
              },
            });
          }),
        );

        // Also optimistically update topic conversations cache (topics list UI)
        const patchTopics = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id: params.creator_id } as any,
            (draft) => {
              const conversationSet = topicSelectors.selectById(draft.conversations, id as string);
              if (conversationSet) {
                topicAdapter.updateOne(draft.conversations, {
                  id: id as string,
                  changes: {
                    ...conversationSet,
                    conversation: {
                      ...conversationSet.conversation,
                      ...patch,
                      // Carefully merge miscellaneous to preserve existing keys
                      ...(patch.miscellaneous
                        ? {
                            miscellaneous: {
                              ...conversationSet.conversation.miscellaneous,
                              ...patch.miscellaneous,
                            },
                          }
                        : {}),
                    },
                  },
                });
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchTopics.undo();
        }
      },
    }),
    deleteConversation: builder.mutation<{ success: boolean; id: string }, string>({
      query(id) {
        return {
          url: `public/v1/forum/conversation/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [QUERY.conversations],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const creator_id = cookieService.getUserId();
        const patchResult = dispatch(
          conversationsApi.util.updateQueryData(
            'getConversations',
            { creator_id } as any,
            (draft) => {
              conversationsAdapter.removeOne(draft.conversations, id);
            },
          ),
        );

        const patchTopic = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id, conversation_type: 'topic' } as any,
            (draft) => {
              conversationsAdapter.removeOne(draft.conversations, id);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
          patchTopic.undo();
        }
      },
    }),
    updateConversationRestrictions: builder.mutation<
      IConversationRestrictionOptions,
      { conversationId: string; restrictions: IConversationRestrictionOptions }
    >({
      query: ({ conversationId, restrictions }) => ({
        url: `public/v1/forum/update_restrictions/${conversationId}`,
        method: 'PUT',
        body: { restrictions },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted({ conversationId, restrictions }, { dispatch, queryFulfilled }) {
        const patchConversationListResult = dispatch(
          conversationsApi.util.updateQueryData('getConversations', {} as any, (draft) => {
            const conversationSet = conversationsSelector.selectById(
              draft.conversations,
              conversationId,
            );
            if (conversationSet) {
              conversationsAdapter.updateOne(draft.conversations, {
                id: conversationId,
                changes: {
                  ...conversationSet,
                  restrictions_list: restrictions,
                },
              });
            }
          }),
        );

        const patchCommunityListResult = dispatch(
          conversationsApi.util.updateQueryData(
            'getCommunityConversations',
            {
              creator_id: cookieService.getUserId(),
            } as any,
            (draft) => {
              const conversationSet = conversationsSelector.selectById(
                draft.conversations,
                conversationId,
              );
              if (conversationSet) {
                conversationsAdapter.updateOne(draft.conversations, {
                  id: conversationId,
                  changes: {
                    ...conversationSet,
                    restrictions_list: restrictions,
                  },
                });
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchConversationListResult.undo();
          patchCommunityListResult.undo();
        }
      },
    }),
    getTheConversationFans: builder.query<INormalizedFansResponse, IConversationFansQueryParams>({
      query: ({ id, ...params }) => ({
        url: `public/v1/forum/fans_of_conversation/${id}`,
        params,
      }),
      providesTags: [QUERY.conversations],
      transformResponse: ({ fans, total_pages, total_records, current_page }) => {
        const initialState = conversationFansAdapter.getInitialState();

        return {
          fans: conversationFansAdapter.setAll(initialState, fans),
          totalPages: total_pages,
          totalRecords: total_records,
          currentPage: current_page,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameConversation = currentArg.id === previousArg?.id;
        const isSamePage = currentArg.page === previousArg?.page;
        const isSameSearch = currentArg.search === previousArg?.search;
        return !isSameConversation || !isSamePage || !isSameSearch;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs?.id}-${queryArgs?.search || ''}`;
      },
      merge: (currentState, incomingState, { arg }) => {
        if (arg.page === 1) {
          return incomingState;
        }
        conversationFansAdapter.addMany(
          currentState.fans,
          conversationFansSelector.selectAll(incomingState.fans),
        );
      },
    }),
    getConversationFans: builder.query<
      { fans: any; total_pages: number },
      { id: string; page: number; items_per_page: number; search?: string }
    >({
      query: ({ id, page, items_per_page, search }) => ({
        url: `public/v1/forum/fans_of_conversation/${id}`,
        params: {
          page,
          items_per_page,
          search,
        },
      }),
      providesTags: [QUERY.conversationFans],
      transformResponse: (response: { fans: IFansOfConverstion[]; total_pages: number }) => {
        const initialState = conversationFansAdapter.getInitialState();
        return {
          fans: conversationFansAdapter.setAll(initialState, response.fans),
          total_pages: response.total_pages,
        };
      },
    }),
    markCreatorConversation: builder.mutation<
      { message: string },
      { conversation_id: string; creator_id: string }
    >({
      query: ({ conversation_id, creator_id }) => ({
        url: `public/v1/forum/mark_creator_conversation`,
        method: 'PUT',
        body: { conversation_id, creator_id },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation],
      async onQueryStarted({ conversation_id, creator_id }, { dispatch, queryFulfilled }) {
        // const patchResult = dispatch(
        //   conversationsApi.util.updateQueryData(
        //     'getConversations',
        //     { creator_id } as any,
        //     (draft) => {
        //       const conversationSet = conversationsSelector.selectById(
        //         draft.conversations,
        //         conversation_id,
        //       );
        //       if (conversationSet) {
        //         conversationsAdapter.updateOne(draft.conversations, {
        //           id: conversation_id,
        //           changes: {
        //             ...conversationSet,
        //             unread_messages_count: 0,
        //             conversation: {
        //               ...conversationSet.conversation,
        //               opened_by_creator: true,
        //             },
        //           },
        //         });
        //       }
        //     },
        //   ),
        // );

        try {
          await queryFulfilled;
        } catch {
          // patchResult.undo();
        }
      },
    }),
    updateConversationParticipants: builder.mutation<
      { message: string; removed_fans?: string[] },
      { conversation_id: string; fan_ids: string[]; creator_id: string; update: 'add' | 'remove' }
    >({
      query: ({ conversation_id, fan_ids, creator_id, update }) => ({
        url: `public/v1/forum/update_conversation_participants/${conversation_id}`,
        method: 'PUT',
        body: { fan_ids, creator_id, update },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.singleConversation, QUERY.conversationFans],
      async onQueryStarted({ conversation_id, fan_ids, update }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(conversationsApi.util.resetApiState());
        } catch (error) {
          console.error('Failed to update conversation participants:', error);
        }
      },
    }),

    reorderConversations: builder.mutation<{ message: string }, IReorderConversationsPayload>({
      query: ({ creator_id, conversation_type, ordered_ids }) => ({
        url: `public/v1/forum/update_conversation_order_index/${creator_id}`,
        method: 'PUT',
        body: {
          conversation_type,
          ordered_ids,
        },
      }),
      invalidatesTags: [QUERY.conversations, QUERY.topics],
      async onQueryStarted(
        { creator_id, conversation_type, ordered_ids },
        { dispatch, queryFulfilled },
      ) {
        if (conversation_type !== 'topic') return;

        const patchResult = dispatch(
          conversationsApi.util.updateQueryData(
            'getTopicConversations',
            { creator_id, page: 1, conversation_type: 'topic' } as any,
            (draft) => {
              const current = topicAdapter.getSelectors().selectAll(draft.conversations);

              // Map current list into a record by ID for quick access
              const mapById = new Map(current.map((conv) => [conv.conversation.id, conv]));

              // Build new ordered list
              const reordered = ordered_ids
                .map((id, index) => {
                  const item = mapById.get(id);
                  if (!item) return null;
                  return {
                    ...item,
                    conversation: {
                      ...item.conversation,
                      order_index: index,
                    },
                  };
                })
                .filter(Boolean) as typeof current;

              // Set all reordered items
              topicAdapter.setAll(draft.conversations, reordered);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (err) {
          patchResult.undo(); // rollback in case of failure
        }
      },
    }),

    findDirectMessage: builder.query({
      query: (params) => ({
        url: `public/v1/forum/find_direct_message/${params.fan_id}`,
        params: {
          creator_id: params.creator_id,
        },
      }),
      providesTags: [QUERY.conversations],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetSingleConversationQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useGetCommunityConversationsQuery,
  useLazyGetCommunityConversationsQuery,
  useUpdateConversationRestrictionsMutation,
  useLazyGetTheConversationFansQuery,
  useGetConversationFansQuery,
  useMarkCreatorConversationMutation,
  useUpdateConversationParticipantsMutation,
  useLazyFindDirectMessageQuery,
  useGetTopicConversationsQuery,
  useReorderConversationsMutation,
} = conversationsApi;
